// Pure schema.org JSON-LD builders. No React, no fs — shared by the server SSR
// head pipeline and client-side react-helmet-async. Each builder returns a
// plain object; serializeJsonLd turns it into a string safe to embed inside a
// <script type="application/ld+json"> tag.

export const SITE_ORIGIN = "https://edgely.trade";
const LOGO_URL = `${SITE_ORIGIN}/logos/edgely-logo-dark.png`;

export function buildOrganizationLd(): Record<string, any> {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Edgely",
    url: SITE_ORIGIN,
    logo: LOGO_URL,
    description:
      "Journal de trading complet avec analytics avancées pour traders actifs et prop firm.",
  };
}

export function buildWebSiteLd(): Record<string, any> {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Edgely",
    url: SITE_ORIGIN,
  };
}

export interface PlanOffer {
  name: string;
  price: string; // numeric string, e.g. "15"
  recurrence: "month" | "year";
}

// The canonical Edgely plan offers used in all SoftwareApplication JSON-LD
// (homepage + marketing feature pages). Single source so structured-data
// pricing never diverges across pages. Prices in EUR.
export const EDGELY_PLAN_OFFERS: PlanOffer[] = [
  { name: "Free", price: "0", recurrence: "month" },
  { name: "Pro", price: "15", recurrence: "month" },
  { name: "Pro Annuel", price: "144", recurrence: "year" },
  { name: "Max", price: "29", recurrence: "month" },
  { name: "Max Annuel", price: "279", recurrence: "year" },
];

export function buildSoftwareApplicationLd(offers: PlanOffer[]): Record<string, any> {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Edgely",
    applicationCategory: "FinanceApplication",
    operatingSystem: "Web",
    url: SITE_ORIGIN,
    offers: offers.map((o) => ({
      "@type": "Offer",
      name: o.name,
      price: o.price,
      priceCurrency: "EUR",
      category: o.recurrence === "year" ? "Annual subscription" : "Monthly subscription",
    })),
  };
}

export interface WebPageLdInput {
  name: string;
  description: string;
  path: string; // e.g. "/vs/tradezella"
}

export function buildWebPageLd(input: WebPageLdInput): Record<string, any> {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: input.name,
    description: input.description,
    url: `${SITE_ORIGIN}${input.path}`,
    isPartOf: { "@type": "WebSite", name: "Edgely", url: SITE_ORIGIN },
  };
}

// Escape the three sequences that can break out of a <script> context or be
// mis-parsed by the HTML tokenizer: </script>, <!--, ]]>.
export function serializeJsonLd(obj: unknown): string {
  return JSON.stringify(obj)
    .replace(/<\/script>/gi, "<\\/script>")
    .replace(/<!--/g, "<\\!--")
    .replace(/]]>/g, "]]\\>");
}

export interface ArticleLdInput {
  title: string;
  slug: string;
  description: string;
  authorName: string;
  publishedAt: string; // ISO
  updatedAt: string; // ISO
  imageUrl: string | null;
}

export function buildArticleLd(a: ArticleLdInput): Record<string, any> {
  const ld: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: a.title,
    description: a.description,
    mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_ORIGIN}/blog/${a.slug}` },
    author: { "@type": "Person", name: a.authorName },
    publisher: buildOrganizationLd(),
    datePublished: a.publishedAt,
    dateModified: a.updatedAt,
  };
  if (a.imageUrl) ld.image = a.imageUrl;
  return ld;
}

export interface BreadcrumbItem {
  name: string;
  url: string;
}

export function buildBreadcrumbLd(items: BreadcrumbItem[]): Record<string, any> {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
}

export interface FaqItem {
  question: string;
  answer: string;
}

export function buildFaqPageLd(items: FaqItem[]): Record<string, any> | null {
  if (items.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.question,
      acceptedAnswer: { "@type": "Answer", text: it.answer },
    })),
  };
}
